import {
  CurecallCopyButton,
  CurecallList,
  CurecallModalContainer,
  ECurecallSize,
  LoadingSpinner,
} from '@honestica/curecall-ui'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { PatientService } from 'src/services/patient.service'
import { TResourceChangeLog } from 'src/types/resource-change-log'
import { getInternationalSortableDateTimeSecondsString } from 'src/utils/date-formatter.util'
import { getFullName } from 'src/utils/string-formatter.util'

type TResourceChangeHistoryModalProps = {
  projectId: number
  patientId: number
  modelName?: string
  recordId?: string
  isOpen: boolean
  onToggle: VoidFunction
}

const ValueCell = ({ value, label }: { value: string; label: string }) => {
  const { t } = useTranslation()
  return (
    <div className="c-flex c-items-center c-gap-2 c-max-w-[300px]">
      <span className="c-truncate c-flex-1" title={value}>
        {value}
      </span>
      <CurecallCopyButton
        ariaLabelText={t('pages.patient-page.resource-change-history.copy-value-aria-label', { label })}
        data={value}
        size={ECurecallSize.SMALL}
      />
    </div>
  )
}

const getAuthorLabel = (t: any, changeLog: TResourceChangeLog): string => {
  if (changeLog.AuthorUser) {
    return getFullName(changeLog.AuthorUser)
  }
  if (changeLog.AuthorPatientWorkflowId) {
    return t('pages.patient-page.resource-change-history.author-workflow', {
      label: changeLog.AuthorPatientWorkflowId,
    })
  }
  if (changeLog.AuthorPatientConversationId) {
    return t('pages.patient-page.resource-change-history.author-conversation', {
      label: changeLog.AuthorPatientConversationId,
    })
  }
  if (changeLog.AuthorInteropLogId) {
    return t('pages.patient-page.resource-change-history.author-interop', {
      label: changeLog.AuthorInteropLogId,
    })
  }
  return t('pages.patient-page.resource-change-history.author-unknown')
}

export const ResourceChangeHistoryModal = ({
  projectId,
  patientId,
  modelName,
  recordId,
  isOpen,
  onToggle,
}: TResourceChangeHistoryModalProps) => {
  const { t } = useTranslation()

  const {
    isLoading,
    error,
    data: changeLogs,
  } = useQuery<TResourceChangeLog[]>({
    queryKey: ['resource-change-logs', projectId, patientId, modelName, recordId],
    queryFn: () =>
      PatientService.getPatientResourceChangeLogs(projectId, patientId, {
        modelName,
        recordId,
      }),
    enabled: isOpen,
  })

  if (isLoading) {
    return (
      <CurecallModalContainer
        closeModalAriaLabel={t('pages.patient-page.resource-change-history.modal-close-aria-label')}
        isOpen={isOpen}
        toggle={onToggle}
        dialogClassName="sm:c-w-[750px] xl:c-w-[900px] [&>div]:c-flex [&>div]:c-flex-col"
        title={t('pages.patient-page.resource-change-history.modal-title')}
      >
        <LoadingSpinner />
      </CurecallModalContainer>
    )
  }

  if (error || !changeLogs) {
    return (
      <CurecallModalContainer
        closeModalAriaLabel={t('pages.patient-page.resource-change-history.modal-close-aria-label')}
        isOpen={isOpen}
        toggle={onToggle}
        dialogClassName="sm:c-w-[750px] xl:c-w-[900px] [&>div]:c-flex [&>div]:c-flex-col"
        title={t('pages.patient-page.resource-change-history.modal-title')}
      >
        <p>{t('pages.patient-page.resource-change-history.error-loading-data')}</p>
      </CurecallModalContainer>
    )
  }

  const tableRowsData =
    changeLogs.length > 0
      ? changeLogs.map((changeLog) => {
          const oldValue = changeLog.oldValue ?? t('pages.patient-page.resource-change-history.empty-value')
          const newValue = changeLog.newValue ?? t('pages.patient-page.resource-change-history.empty-value')
          return {
            id: changeLog.id,
            date: getInternationalSortableDateTimeSecondsString(new Date(changeLog.createdAt)),
            author: getAuthorLabel(t, changeLog),
            resource: `${changeLog.modelName}(${changeLog.recordId})`,
            field: changeLog.fieldName,
            oldValue: (
              <ValueCell
                value={oldValue}
                label={t('pages.patient-page.resource-change-history.old-value-column')}
              />
            ),
            newValue: (
              <ValueCell
                value={newValue}
                label={t('pages.patient-page.resource-change-history.new-value-column')}
              />
            ),
            wasChangedByUser: !!changeLog.AuthorUser,
          }
        })
      : []

  return (
    <CurecallModalContainer
      closeModalAriaLabel={t('pages.patient-page.resource-change-history.modal-close-aria-label')}
      isOpen={isOpen}
      toggle={onToggle}
      dialogClassName="sm:c-w-[750px] xl:c-w-[900px] [&>div]:c-flex [&>div]:c-flex-col"
      title={t('pages.patient-page.resource-change-history.modal-title')}
    >
      {tableRowsData.length > 0 ? (
        <CurecallList
          rowsData={tableRowsData as any}
          headersData={[
            {
              label: t('pages.patient-page.resource-change-history.date-column'),
              key: 'date',
              isSorted: true,
            },
            {
              label: t('pages.patient-page.resource-change-history.author-column'),
              key: 'author',
              isSorted: true,
            },
            {
              label: t('pages.patient-page.resource-change-history.resource-column'),
              key: 'resource',
              isSorted: true,
            },
            {
              label: t('pages.patient-page.resource-change-history.field-column'),
              key: 'field',
              isSorted: true,
            },
            {
              label: t('pages.patient-page.resource-change-history.old-value-column'),
              key: 'oldValue',
              isSorted: true,
            },
            {
              label: t('pages.patient-page.resource-change-history.new-value-column'),
              key: 'newValue',
              isSorted: true,
            },
          ]}
          isSelectedRows={{ matchingHeader: 'wasChangedByUser', matchingValue: true }}
        />
      ) : (
        <p>{t('pages.patient-page.resource-change-history.no-data')}</p>
      )}
    </CurecallModalContainer>
  )
}
